import React, { useState, useEffect, useRef, PropsWithChildren, forwardRef } from 'react'
import { CSVLink } from "react-csv";
import { handleDeNorm, normalizePhone, deNormalizePhone, handleNorm } from '../../utils/functions/tools/normalizePhone';
import {
    DataTable,
    Table,
    Link,
    Button,
    Pagination,
    TableBatchAction,
    TableBatchActions,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Tag,
    Modal,
    TextInput,
    Select,
    SelectItem,
    Form,
    DatePicker,
    DatePickerInput,
    OverflowMenu,
    OverflowMenuItem,
    InlineNotification,
    NotificationActionButton
} from 'carbon-components-react';
import {
    Delete16 as Delete,
    Save16 as Save,
    Download16 as Download,
    Edit32 as Edit,
} from '@carbon/icons-react';

const clientPhoneHeaders = [
    { header: "ID", key: "id" },
    { header: "Type", key: "type" },
    { header: "Action", key: "action" },
    { header: "Start Date", key: "startDate" },
    { header: "End Date", key: "endDate" },
    { header: "Phone Number", key: "phoneNumber" },
];

export default {
    title: 'Components/DataTable/Batch Actions',
    component: DataTable,
    subcomponents: {
        TableBatchAction,
        TableBatchActions,
        TableToolbar,
        TableToolbarAction,
        TableToolbarContent,
        TableToolbarSearch,
        TableToolbarMenu,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableHeader,
        TableRow,
    },
};

export const ClientPhoneNumbers = forwardRef(({ setPhonesToDel, phonesToDel, setOpenDeletePhoneModal, openDeletePhoneModal, editPhoneInput, setEditPhoneInput, deletePhone, phoneInput, setPhoneInput, curDate, addPhoneForm, addPhone, countries, rows, totalPhoneItems, currentPhonePageSize, setPhoneCurrentPageSize, setFirstRowPhoneIndex, openAddPhoneModal, setOpenAddPhoneModal, openEditPhoneModal, setOpenEditPhoneModal, editPhoneRecord, editPhoneSetup, editPhone, editPhoneForm, editError, setEditError }: any, ref) => (
    <>
        <DataTable rows={rows} headers={clientPhoneHeaders} isSortable>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getToolbarProps,
                getBatchActionProps,
                getTableProps,
                getTableContainerProps,
            }) => {
                const batchActionProps = getBatchActionProps();
                return (
                    <TableContainer
                        className="mt-1"
                        {...getTableContainerProps()}>
                        <TableToolbar {...getToolbarProps()}>
                            <TableToolbarContent
                                aria-hidden={batchActionProps.shouldShowBatchActions}>
                                <Button
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    size="small"
                                    kind="primary"
                                    onClick={() => setOpenAddPhoneModal(true)}>
                                    Add new
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, i) => (
                                        <TableHeader key={i} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} {...getRowProps({ row })}>
                                        {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                                {cell.info.header === "phoneNumber" ? <p>{normalizePhone(cell.value)}</p>
                                                    : cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                                                        <OverflowMenuItem onClick={() => setOpenEditPhoneModal(true) & editPhoneSetup(row.id)} itemText="Edit" />
                                                        <OverflowMenuItem onClick={() => setPhonesToDel(row.id) & setOpenDeletePhoneModal(true)} hasDivider isDelete itemText="Delete" />
                                                    </OverflowMenu>
                                                        : cell.info.header === "type" ? <p>{cell.value.type}</p>
                                                            : cell.value}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            totalItems={totalPhoneItems}
                            pageSizes={[10, 20, 30, 40, 50]}
                            pageSize={currentPhonePageSize}
                            onChange={({ page, pageSize }) => {
                                if (pageSize !== currentPhonePageSize) {
                                    setPhoneCurrentPageSize(pageSize);
                                }
                                setFirstRowPhoneIndex(pageSize * (page - 1));
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>

        <Modal
            open={openAddPhoneModal}
            modalHeading="Add phone number"
            modalLabel="Client Phone Numbers"
            primaryButtonText="Add"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenAddPhoneModal(false) & setEditError(false)}
            onRequestSubmit={() => addPhoneForm.current && addPhoneForm.current.click() & setEditError(false)}
        >   {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add client phone number" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to add a new phone number. You may add new Home, Business, Mobile, or Fax numbers.
            </p>
            <Form onSubmit={(values) => addPhone(values)} className="d-flex flex-w">
            <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            required
                            id="startDate"
                            name="startDate"
                            placeholder="yyyy-mm-dd"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                            defaultValue={curDate}
                            labelText="Start date"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            id="endDate"
                            name="endDate"
                            placeholder="yyyy-mm-dd"
                            labelText="End date"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 m-005">
                    <Select id="type" name="type" defaultValue="2" labelText="Phone Type" style={{ marginBottom: '1rem' }}>
                        <SelectItem value="1" text="Home" />
                        <SelectItem value="2" text="Business" />
                        <SelectItem value="3" text="Mobile" />
                        <SelectItem value="4" text="Business Mobile" />
                        <SelectItem value="5" text="Fax" />
                        <SelectItem value="6" text="Business Fax" />
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="phoneNumber"
                        name="phoneNumber"
                        labelText="Phone Number"
                        placeholder="Enter phone number"
                        invalidText="Phone Number is required"
                        onChange={((e) => setPhoneInput(normalizePhone(e.target.value)))}
                        value={phoneInput}
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={addPhoneForm} />
            </Form>
        </Modal>

        <Modal
            open={openEditPhoneModal}
            modalHeading="Edit phone number"
            modalLabel="Client Phone Numbers"
            primaryButtonText="Save Changes"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenEditPhoneModal(false) & setEditError(false)}
            onRequestSubmit={() => editPhoneForm.current && editPhoneForm.current.click() & setEditError(false)}
        >   {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to edit client phone number" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to edit the selected phone number.
            </p>
            <Form onSubmit={(values) => editPhone(values)} className="d-flex flex-w">
            <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            required
                            id="startDate"
                            name="startDate"
                            placeholder="yyyy-mm-dd"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                            defaultValue={editPhoneRecord?.startDate}
                            labelText="Start date"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            id="endDate"
                            name="endDate"
                            placeholder="yyyy-mm-dd"
                            defaultValue={editPhoneRecord?.endDate}
                            labelText="End date"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 m-005">
                    <Select id="type" name="type" defaultValue="2" labelText="Phone Type" style={{ marginBottom: '1rem' }}>
                        <SelectItem selected={editPhoneRecord?.type.type === 'Home' ? "selected" : ""} value="1" text="Home" />
                        <SelectItem selected={editPhoneRecord?.type.type === 'Business' ? "selected" : ""} value="2" text="Business" />
                        <SelectItem selected={editPhoneRecord?.type.type === 'Mobile' ? "selected" : ""} value="3" text="Mobile" />
                        <SelectItem selected={editPhoneRecord?.type.type === 'Business Mobile' ? "selected" : ""} value="4" text="Business Mobile" />
                        <SelectItem selected={editPhoneRecord?.type.type === 'Fax' ? "selected" : ""} value="5" text="Fax" />
                        <SelectItem selected={editPhoneRecord?.type.type === 'Business Fax' ? "selected" : ""} value="6" text="Business Fax" />
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="phoneNumber"
                        name="phoneNumber"
                        labelText="Phone Number"
                        placeholder="Enter phone number"
                        invalidText="Phone number is required"
                        onChange={((e) => setEditPhoneInput(normalizePhone(e.target.value)))}
                        value={normalizePhone(editPhoneInput)}
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={editPhoneForm} />
            </Form>
        </Modal>

        <Modal
            danger
            open={openDeletePhoneModal}
            modalHeading="Delete Client Phone Number"
            modalLabel="Client Phone Numbers"
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
            onRequestClose={() => setOpenDeletePhoneModal(false) & setEditError(false)}
            onRequestSubmit={() => deletePhone(phonesToDel)}
        >
            {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete client phone number" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p>Are you sure you want to delete the selected client phone number?</p>

        </Modal>
    </>
));